import React, { useEffect, useState, useCallback } from 'react';
import { DollarSign, TrendingUp, Activity, BarChart3, X, AlertTriangle, Check, Send, ExternalLink } from 'lucide-react';
import api from '../api/client';
import toast from 'react-hot-toast';

const fmt = (n) => '$' + (parseFloat(n) || 0).toFixed(2);
const pct = (n) => (parseFloat(n) || 0).toFixed(1) + '%';

const statusColors = {
  open: 'bg-blue-100 text-blue-700',
  closed: 'bg-gray-100 text-gray-700',
  invoiced: 'bg-orange-100 text-orange-700',
  paid: 'bg-green-100 text-green-700',
};

const marginColor = (margin, billed) => {
  if (!billed || billed === 0) return 'text-gray-500';
  const p = (margin / billed) * 100;
  if (p >= 20) return 'text-green-600';
  if (p >= 10) return 'text-yellow-600';
  return 'text-red-600';
};

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
      <div className="flex items-center gap-3">
        <div className={`p-2.5 rounded-lg ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-xs text-gray-500">{label}</p>
          <p className="text-xl font-bold text-gray-900">{value}</p>
          {sub && <p className="text-xs text-gray-400">{sub}</p>}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════
// Cycle Detail Modal
// ════════════════════════════════
function CycleDetailModal({ cycleId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [stripeConfigured, setStripeConfigured] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get(`/platform/billing/cycles/${cycleId}`),
      api.get('/platform/stripe/config').catch(() => ({ data: { configured: false } })),
    ]).then(([cycleRes, configRes]) => {
      setData(cycleRes.data);
      setStripeConfigured(configRes.data?.configured || false);
    }).catch(() => toast.error('Failed to load cycle'))
      .finally(() => setLoading(false));
  }, [cycleId]);

  const handleClose = async () => {
    if (!window.confirm('Close this billing cycle? This will aggregate all usage into line items.')) return;
    try {
      await api.post(`/platform/billing/cycles/${cycleId}/close`);
      toast.success('Cycle closed');
      // Reload
      const res = await api.get(`/platform/billing/cycles/${cycleId}`);
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to close cycle');
    }
  };

  const handleSendInvoice = async () => {
    if (!window.confirm('Send a Stripe invoice for this billing cycle?')) return;
    setSendingInvoice(true);
    try {
      const res = await api.post(`/platform/billing/cycles/${cycleId}/send-invoice`);
      if (res.data.error) {
        toast.error(res.data.error);
      } else if (res.data.already_invoiced) {
        toast.success('Invoice already exists');
      } else {
        toast.success('Invoice sent successfully');
      }
      // Reload cycle data
      const updated = await api.get(`/platform/billing/cycles/${cycleId}`);
      setData(updated.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send invoice');
    } finally {
      setSendingInvoice(false);
    }
  };

  if (loading) return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-8 w-[700px]">Loading...</div>
    </div>
  );
  if (!data) return null;

  const totalProviderCost = data.line_items.reduce((s, li) => s + li.provider_cost, 0);
  const totalBilled = data.line_items.reduce((s, li) => s + li.billed, 0);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-[750px] max-h-[85vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{data.tenant.name} — Billing Cycle</h2>
            <p className="text-sm text-gray-500">
              {new Date(data.period.start).toLocaleDateString()} — {new Date(data.period.end).toLocaleDateString()}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusColors[data.status] || 'bg-gray-100'}`}>
              {data.status}
            </span>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="px-6 py-4">
          {data.line_items.length === 0 ? (
            <p className="text-sm text-gray-500 py-8 text-center">
              {data.status === 'open' ? 'No usage recorded yet in this cycle.' : 'No line items.'}
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Item</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Type</th>
                  <th className="text-right px-3 py-2 font-medium text-gray-600">Qty</th>
                  <th className="text-right px-3 py-2 font-medium text-gray-600">Provider Cost</th>
                  <th className="text-right px-3 py-2 font-medium text-gray-600">Markup</th>
                  <th className="text-right px-3 py-2 font-medium text-gray-600">Billed</th>
                  <th className="text-right px-3 py-2 font-medium text-gray-600">Margin</th>
                </tr>
              </thead>
              <tbody>
                {data.line_items.map((li, i) => {
                  const margin = li.billed - li.provider_cost;
                  const typeColors = {
                    base_plan: 'bg-purple-100 text-purple-700',
                    addon: 'bg-blue-100 text-blue-700',
                    usage: 'bg-gray-100 text-gray-600',
                    overage: 'bg-red-100 text-red-700',
                  };
                  const typeLabel = { base_plan: 'Plan', addon: 'Add-on', usage: 'Usage', overage: 'Overage' };
                  const liType = li.line_item_type || 'usage';
                  return (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="px-3 py-2 font-medium text-gray-900">{li.service_name}</td>
                      <td className="px-3 py-2">
                        <span className={`px-1.5 py-0.5 text-xs font-medium rounded-full ${typeColors[liType] || typeColors.usage}`}>
                          {typeLabel[liType] || liType}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right text-gray-700">{li.usage_count.toLocaleString()} <span className="text-xs text-gray-400">{li.usage_unit}</span></td>
                      <td className="px-3 py-2 text-right text-gray-700">{fmt(li.provider_cost)}</td>
                      <td className="px-3 py-2 text-right text-gray-700">{liType === 'base_plan' || liType === 'addon' ? '—' : pct(li.markup_pct)}</td>
                      <td className="px-3 py-2 text-right font-medium text-gray-900">{fmt(li.billed)}</td>
                      <td className={`px-3 py-2 text-right font-medium ${marginColor(margin, li.billed)}`}>{fmt(margin)}</td>
                    </tr>
                  );
                })}
                <tr className="bg-gray-50 font-semibold">
                  <td className="px-3 py-2" colSpan={2}>Total</td>
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2 text-right">{fmt(totalProviderCost)}</td>
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2 text-right">{fmt(totalBilled)}</td>
                  <td className={`px-3 py-2 text-right ${marginColor(totalBilled - totalProviderCost, totalBilled)}`}>{fmt(totalBilled - totalProviderCost)}</td>
                </tr>
              </tbody>
            </table>
          )}

          {data.reconciled_at && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
              <div className="flex items-center gap-2 text-green-700 font-medium">
                <Check className="w-4 h-4" /> Reconciled on {new Date(data.reconciled_at).toLocaleDateString()}
              </div>
              {data.reconciliation_notes && <p className="text-green-600 mt-1">{data.reconciliation_notes}</p>}
            </div>
          )}
        </div>

        {/* Stripe Invoice Info */}
        {data.stripe_invoice_url && (
          <div className="px-6 py-2 bg-blue-50 border-t border-blue-100 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-blue-700">
              <Send className="w-4 h-4" />
              <span>Stripe Invoice: <span className="font-mono">{data.stripe_invoice_id}</span></span>
              {data.stripe_payment_status && (
                <span className={`px-1.5 py-0.5 text-xs font-medium rounded-full ${
                  data.stripe_payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                  data.stripe_payment_status === 'failed' ? 'bg-red-100 text-red-700' :
                  'bg-yellow-100 text-yellow-700'
                }`}>{data.stripe_payment_status}</span>
              )}
            </div>
            <a href={data.stripe_invoice_url} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium">
              View Invoice <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        )}

        <div className="px-6 py-3 border-t border-gray-200 flex justify-end gap-2">
          {data.status === 'open' && (
            <button onClick={handleClose} className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              Close Cycle
            </button>
          )}
          {data.status === 'closed' && stripeConfigured && !data.stripe_invoice_id && (
            <button onClick={handleSendInvoice} disabled={sendingInvoice}
              className="flex items-center gap-1 px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
              <Send className="w-4 h-4" /> {sendingInvoice ? 'Sending...' : 'Send Invoice'}
            </button>
          )}
          {data.status === 'closed' && !data.reconciled_at && (
            <ReconcileButton cycleId={cycleId} lineItems={data.line_items} onDone={async () => {
              const res = await api.get(`/platform/billing/cycles/${cycleId}`);
              setData(res.data);
            }} />
          )}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════
// Reconciliation Button + Modal
// ════════════════════════════════
function ReconcileButton({ cycleId, lineItems, onDone }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(lineItems.map(li => ({ ...li, actual_cost: li.provider_cost.toFixed(2) })));
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post(`/platform/billing/cycles/${cycleId}/reconcile`, {
        line_items: items.map(li => ({
          line_item_id: li.id || li.service_slug,
          actual_provider_cost: parseFloat(li.actual_cost) || 0,
        })),
        notes,
      });
      toast.success('Reconciliation saved');
      setOpen(false);
      onDone();
    } catch (err) {
      toast.error('Failed to save reconciliation');
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="px-4 py-2 text-sm font-medium bg-orange-600 text-white rounded-lg hover:bg-orange-700">
        Reconcile
      </button>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]" onClick={() => setOpen(false)}>
      <div className="bg-white rounded-xl shadow-2xl w-[600px] max-h-[80vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">Reconcile Provider Costs</h3>
          <p className="text-sm text-gray-500">Enter actual provider costs from invoices</p>
        </div>
        <div className="px-6 py-4">
          <table className="w-full text-sm mb-4">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-gray-600">Service</th>
                <th className="text-right px-3 py-2 font-medium text-gray-600">Tracked</th>
                <th className="text-right px-3 py-2 font-medium text-gray-600">Actual Cost</th>
                <th className="text-right px-3 py-2 font-medium text-gray-600">Variance</th>
              </tr>
            </thead>
            <tbody>
              {items.map((li, i) => {
                const variance = (parseFloat(li.actual_cost) || 0) - li.provider_cost;
                return (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="px-3 py-2 font-medium">{li.service_name}</td>
                    <td className="px-3 py-2 text-right text-gray-500">{fmt(li.provider_cost)}</td>
                    <td className="px-3 py-2 text-right">
                      <input type="number" step="0.01" value={li.actual_cost}
                        onChange={e => {
                          const updated = [...items];
                          updated[i] = { ...updated[i], actual_cost: e.target.value };
                          setItems(updated);
                        }}
                        className="w-24 px-2 py-1 border border-gray-300 rounded text-sm text-right focus:outline-none focus:ring-1 focus:ring-brand-500" />
                    </td>
                    <td className={`px-3 py-2 text-right font-medium ${variance > 0 ? 'text-red-600' : variance < 0 ? 'text-green-600' : 'text-gray-500'}`}>
                      {variance > 0 ? '+' : ''}{fmt(variance)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Reconciliation notes..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" rows={2} />
        </div>
        <div className="px-6 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 text-sm font-medium bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50">
            {saving ? 'Saving...' : 'Save Reconciliation'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════
// Main Billing Page
// ════════════════════════════════
export default function Billing() {
  const [summary, setSummary] = useState(null);
  const [cycles, setCycles] = useState({ cycles: [], pagination: {} });
  const [cycleFilter, setCycleFilter] = useState('all');
  const [selectedCycleId, setSelectedCycleId] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [sumRes, cycleRes] = await Promise.all([
        api.get('/platform/billing/summary'),
        api.get('/platform/billing/cycles', { params: { limit: 20, status: cycleFilter !== 'all' ? cycleFilter : undefined } }),
      ]);
      setSummary(sumRes.data);
      setCycles(cycleRes.data);
    } catch (err) {
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  }, [cycleFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-48 bg-gray-200 rounded" />
          <div className="grid grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => <div key={i} className="h-20 bg-gray-200 rounded-xl" />)}
          </div>
          <div className="h-64 bg-gray-200 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!summary) return null;

  const cm = summary.current_month;
  const maxBar = Math.max(...(summary.last_6_months.map(m => m.billed) || [1]), cm.total_billed || 1, 1);

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Billing</h1>

      {/* Section 1: Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={DollarSign} label="Revenue (This Month)" value={fmt(cm.total_billed)} color="bg-green-600" />
        <StatCard icon={TrendingUp} label="Provider Cost" value={fmt(cm.total_provider_cost)} color="bg-red-500" />
        <StatCard icon={BarChart3} label="Margin" value={fmt(cm.total_margin)}
          sub={cm.total_billed > 0 ? pct(cm.margin_pct) : '—'} color="bg-blue-600" />
        <StatCard icon={Activity} label="Active Cycles" value={summary.active_cycles} color="bg-purple-600" />
      </div>

      {/* Section 2: Revenue Trend (6 months) */}
      {summary.last_6_months.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Revenue Trend (6 Months)</h2>
          <div className="flex items-end gap-2 h-40">
            {summary.last_6_months.map((m, i) => {
              const billedH = (m.billed / maxBar) * 100;
              const costH = (m.provider_cost / maxBar) * 100;
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full flex items-end gap-0.5 h-32">
                    <div className="flex-1 bg-green-200 rounded-t" style={{ height: `${billedH}%` }} title={`Billed: ${fmt(m.billed)}`}>
                      <div className="bg-green-500 rounded-t h-full opacity-80" />
                    </div>
                    <div className="flex-1 bg-red-200 rounded-t" style={{ height: `${costH}%` }} title={`Cost: ${fmt(m.provider_cost)}`}>
                      <div className="bg-red-400 rounded-t h-full opacity-80" />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">{m.month.slice(5)}</p>
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-500 rounded" /> Revenue</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-400 rounded" /> Cost</span>
          </div>
        </div>
      )}

      {/* Section 3: Revenue by Service */}
      {cm.by_service.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Revenue by Service (Current Month)</h2>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Service</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Usage</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Provider Cost</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Billed</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Margin</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Margin %</th>
                <th className="px-4 py-3 w-32"></th>
              </tr>
            </thead>
            <tbody>
              {cm.by_service.filter(s => s.usage > 0).map((s, i) => {
                const marginPct = s.billed > 0 ? (s.margin / s.billed) * 100 : 0;
                return (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="px-4 py-3 font-medium text-gray-900">{s.service}</td>
                    <td className="px-4 py-3 text-right text-gray-700">{s.usage.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right text-gray-700">{fmt(s.provider_cost)}</td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">{fmt(s.billed)}</td>
                    <td className={`px-4 py-3 text-right font-medium ${marginColor(s.margin, s.billed)}`}>{fmt(s.margin)}</td>
                    <td className={`px-4 py-3 text-right font-medium ${marginColor(s.margin, s.billed)}`}>{pct(marginPct)}</td>
                    <td className="px-4 py-3">
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div className={`h-1.5 rounded-full ${marginPct >= 20 ? 'bg-green-500' : marginPct >= 10 ? 'bg-yellow-500' : 'bg-red-500'}`}
                          style={{ width: `${Math.min(marginPct, 100)}%` }} />
                      </div>
                    </td>
                  </tr>
                );
              })}
              <tr className="bg-gray-50 font-semibold">
                <td className="px-4 py-3">Total</td>
                <td className="px-4 py-3 text-right">{cm.by_service.reduce((s, r) => s + r.usage, 0).toLocaleString()}</td>
                <td className="px-4 py-3 text-right">{fmt(cm.total_provider_cost)}</td>
                <td className="px-4 py-3 text-right">{fmt(cm.total_billed)}</td>
                <td className={`px-4 py-3 text-right ${marginColor(cm.total_margin, cm.total_billed)}`}>{fmt(cm.total_margin)}</td>
                <td className={`px-4 py-3 text-right ${marginColor(cm.total_margin, cm.total_billed)}`}>{cm.total_billed > 0 ? pct(cm.margin_pct) : '—'}</td>
                <td className="px-4 py-3"></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Section 4: Billing Cycles */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Billing Cycles</h2>
          <select value={cycleFilter} onChange={e => setCycleFilter(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
            <option value="invoiced">Invoiced</option>
            <option value="paid">Paid</option>
          </select>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Tenant</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Period</th>
              <th className="text-center px-4 py-3 font-medium text-gray-600">Status</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Provider Cost</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Billed</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Margin</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Items</th>
            </tr>
          </thead>
          <tbody>
            {cycles.cycles.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-gray-400">No billing cycles found</td></tr>
            ) : (
              cycles.cycles.map(c => {
                const margin = parseFloat(c.total_billed || 0) - parseFloat(c.total_provider_cost || 0);
                return (
                  <tr key={c.id} onClick={() => setSelectedCycleId(c.id)}
                    className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{c.tenant_name}</p>
                      <p className="text-xs text-gray-500">{c.tenant_slug}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {new Date(c.cycle_start).toLocaleDateString()} — {new Date(c.cycle_end).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${statusColors[c.status] || 'bg-gray-100'}`}>
                        {c.status}
                      </span>
                      {c.reconciled_at && <Check className="inline w-3 h-3 text-green-600 ml-1" />}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">{fmt(c.total_provider_cost)}</td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">{fmt(c.total_billed)}</td>
                    <td className={`px-4 py-3 text-right font-medium ${marginColor(margin, parseFloat(c.total_billed || 0))}`}>{fmt(margin)}</td>
                    <td className="px-4 py-3 text-right text-gray-500">{c.line_item_count}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Cycle Detail Modal */}
      {selectedCycleId && (
        <CycleDetailModal
          cycleId={selectedCycleId}
          onClose={() => { setSelectedCycleId(null); fetchData(); }}
        />
      )}
    </div>
  );
}
